/**
 * <s-announcement> — rotates announcement bar messages, one at a time, with a crossfade.
 *
 * Each `[data-slide]` child shares one grid cell; only the active one is visible and exposed to
 * assistive tech. Rotation pauses while hovered or focused and in hidden tabs. The fade is CSS
 * (motion-safe), so reduced-motion visitors get an instant swap.
 *
 * Expected DOM (rendered in layout/theme.liquid):
 *   <s-announcement data-interval="4000">
 *     <div data-slide>…</div>
 *     <div data-slide class="opacity-0" aria-hidden="true">…</div>
 *   </s-announcement>
 */
class SAnnouncement extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll("[data-slide]"));
    if (this.slides.length < 2) return;

    this.index = 0;
    this.interval = Number(this.dataset.interval) || 4000;
    this.paused = false;
    this.abort = new AbortController();
    const { signal } = this.abort;

    const pause = () => { this.paused = true; };
    const resume = () => { this.paused = false; };
    this.addEventListener("pointerenter", pause, { signal });
    this.addEventListener("pointerleave", resume, { signal });
    this.addEventListener("focusin", pause, { signal });
    this.addEventListener("focusout", resume, { signal });

    this.timer = window.setInterval(() => {
      if (this.paused || document.hidden) return;
      this.show((this.index + 1) % this.slides.length);
    }, this.interval);
  }

  disconnectedCallback() {
    this.abort?.abort();
    if (this.timer) window.clearInterval(this.timer);
  }

  /** @param {number} next */
  show(next) {
    this.slides.forEach((slide, i) => {
      const active = i === next;
      slide.classList.toggle("opacity-0", !active);
      slide.classList.toggle("pointer-events-none", !active);
      slide.toggleAttribute("aria-hidden", !active);
    });
    this.index = next;
  }
}

if (!customElements.get("s-announcement")) {
  customElements.define("s-announcement", SAnnouncement);
}
